import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { environment } from 'src/environments/environment';
import { UserSource } from 'src/types';
import z from 'zod';

const invalidCsrfSchema = z.object({
  message: z.literal('CSRF token mismatch.'),
});

const invalidRoleSchema = z.object({
  code: z.literal('invalid-role'),
});

export const mfaRequiredSchema = z.object({
  error: z.literal('mfa_required'),
});

export type RegisterParams = {
  email: string;
  is_terms_accepted: boolean;
  is_privacy_accepted: boolean;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
};

export type VerifyEmailParams = {
  name: string;
  password: string;
  is_newsletter_enabled: boolean;
  token: string;
  phone: string;
};

export type SendPasswordResetEmailParams = {
  email: string;
};

export type ResetPasswordParams = {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
};

export type ForceResetPasswordParams = {
  old_password: string;
  password: string;
  password_confirmation: string;
};

export type LoginWithShortTokenResponse = {
  origin: UserSource;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);

  private _origin: UserSource | null = null;
  get origin(): UserSource | null { return this._origin; };

  get next(): string | null { return localStorage.getItem('next') || null; }

  readonly loggedOut$ = new Subject<void>();

  logoutOnUnauthorized = true;

  constructor() {
    axios.defaults.withCredentials = true;
    axios.defaults.withXSRFToken = true;

    this.csrf();

    this.setDefaultUserOrigin();
    this.setRequestInterceptor();
    this.setResponseInterceptor();

    this.setAfterLoginRedirectObserver();
  }

  async login(
    email: string,
    password: string,
    remember_me = false,
    navigateAfterLogin = true,
  ): Promise<void> {
    const url = environment.baseUrl + `/api/auth/login`;
    await axios.post(url, {
      email,
      password,
      remember_me,
    });

    if (!navigateAfterLogin) {
      return;
    }

    if (!this.next) {
      this.setNext(environment.afterLoginPath);
    }

    try {
      await axios.get(environment.baseUrl + `/api/user`);
    } catch (error) {
      if (error instanceof AxiosError && mfaRequiredSchema.safeParse(error.response?.data).success) {
        await this.router.navigateByUrl('2fa/verify');
        return;
      }
      throw error;
    }

    this.router.navigateByUrl(this.next!);
  }

  async loginWithShortToken(shortToken: string): Promise<void> {
    const url = `${environment.baseUrl}/api/auth/login-with-short-token/${shortToken}`;
    const result = await axios.post<LoginWithShortTokenResponse>(url);

    this.setOrigin(result.data.origin);
  }

  // Login for partners
  async loginWithCustomToken(_customToken: string, _origin: UserSource = 'ORGANIC'): Promise<void> {
    throw new Error('Unimplemented');
  }

  async createPayeeAccountFromCashbook(_ssoToken: string): Promise<string> {
    throw new Error('Unimplemented');
  }

  async register(params: RegisterParams): Promise<void> {
    const url = environment.baseUrl + `/api/auth/register`;
    await axios.post(url, params);


    await this.router.navigateByUrl('verify-email');
  }

  async sendPasswordResetEmail(params: SendPasswordResetEmailParams): Promise<void> {
    try {
      const url = environment.baseUrl + `/api/auth/send-reset-password-email`;
      await axios.post(url, params);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        this.snackbar.open('Próbáld újra késöbb!', 'OK', {
          duration: 10000,
        });
      } else {
        console.error('Unknown error while sending password reset email!', error);
        this.snackbar.open('Valami hiba történt', 'OK', {
          duration: 10000,
        });
      }
      throw error;
    }
  }

  async resendVerifyEmail(): Promise<void> {
    const url = environment.baseUrl + `/api/auth/send-email-verification`;
    await axios.post(url);
  }

  async logout(): Promise<void> {
    this.logoutOnUnauthorized = false;
    const url = environment.baseUrl + `/api/auth/logout`;
    await axios.post(url).catch(e => console.warn('Error while logging out', e));
    this.logoutOnUnauthorized = true;

    this.loggedOut$.next();

    await this.router.navigateByUrl('login');
  }

  async resetPassword(params: ResetPasswordParams, redirectTo = 'forgot-password-new-password-success'): Promise<void> {
    const url = environment.baseUrl + `/api/auth/reset-password`;
    await axios.post(url, params);

    await this.router.navigateByUrl(redirectTo);
  }

  async forceResetPassword(params: ForceResetPasswordParams): Promise<void> {
    const url = environment.baseUrl + `/api/auth/force-reset-password`;
    await axios.post(url, params);
  }

  async finalizeSignup(
    params: VerifyEmailParams,
    email: string,
  ): Promise<void> {
    const url = environment.baseUrl + `/api/auth/verify-email`;
    await axios.post(url, params);
    await this.login(email, params.password, false, false);
  }

  async enable2FA(): Promise<void> {
    const url = environment.baseUrl + `/api/auth/2fa/register`;

    await axios.post(url);
    this.setNext(this.router.url);
    this.router.navigateByUrl('2fa/verify');
  }

  async register2FA(): Promise<{ otpauth_url: string; secret: string; }> {
    const url = environment.baseUrl + `/api/auth/2fa/register`;
    const response = await axios.post<{ otpauth_url: string; secret: string; }>(url);
    return response.data;
  }

  async verify2FACode(code: string): Promise<void> {
    const url = environment.baseUrl + `/api/auth/2fa/token`;
    await axios.post(url, { one_time_password: code });
  }

  async disable2FA(): Promise<void> {
    const url = environment.baseUrl + `/api/auth/2fa/disable`;
    await axios.post(url);
  }

  async submitMFACode(code: string): Promise<void> {
    const url = environment.baseUrl + `/api/auth/2fa/token`;

    await axios.post(url, { one_time_password: code });
    const next = this.next || '/user';
    this.router.navigateByUrl(next);
  }

  async resendMFACode(): Promise<void> {
    await axios.post(environment.baseUrl + `/api/auth/2fa/resend`);
  };

  getOriginOfPartnerId(partnerId: string): UserSource | null {
    switch (partnerId) {
      case 'szamlazzhu': return 'PLUGIN_SZAMLAZZHU';
      default: return null;
    }
  }

  setOrigin(origin: UserSource | null): void {
    this._origin = origin;
    if (origin === null) {
      sessionStorage.removeItem('user-origin');
    } else {
      sessionStorage.setItem('user-origin', origin);
    }
  }

  getUserOrigin() {
    return sessionStorage.getItem('user-origin') as UserSource;
  }

  setNext(url: string | null): void {
    if (!url) {
      localStorage.removeItem('next');
    } else {
      localStorage.setItem('next', url);
    }
  }

  private setDefaultUserOrigin(): void {
    let userOrigin = sessionStorage.getItem('user-origin') as UserSource;
    if (userOrigin) {
      this.setOrigin(userOrigin);
    } else {
      userOrigin = sessionStorage.getItem('user-origin') as UserSource;
      if (userOrigin) {
        this.setOrigin(userOrigin);
      }
    }
  }

  private setRequestInterceptor(client: AxiosInstance = axios): void {
    client.interceptors.request.use(
      async config => {
        if (!config.headers.hasAccept()) {
          config.headers.setAccept('application/json');
        }

        if (config.method === 'PUT') {
          config.method = 'POST';
          if (config.data instanceof FormData) {
            config.data.append('_method', 'PUT');
          } else if (typeof config.data === 'object') {
            config.data._method = 'PUT';
          }
        }

        config.headers.set('Accept-Language', this.translate.currentLang || this.translate.defaultLang);

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );
  }

  private setResponseInterceptor(client: AxiosInstance = axios): void {
    client.interceptors.response.use(
      async res => {
        return res;
      },
      async (error: AxiosError) => {
        try {
          if (error.response?.status === 401) {
            if (this.logoutOnUnauthorized) {
              this.dialog.closeAll();
              await this.logout();
              return Promise.reject(error);
            } else {
              this.loggedOut$.next();
              return Promise.reject(error);
            }
          }

          if (
            invalidCsrfSchema.safeParse(error.response?.data).success
            && error.config
          ) {
            await this.csrf();
            return await axios(error.config);
          }

          if (mfaRequiredSchema.safeParse(error.response?.data).success) {
            if (!this.router.url.includes('/2fa/')) {
              if (!this.next) {
                this.setNext(this.router.url);
              }
              await this.router.navigateByUrl('2fa/verify');
            }
            return Promise.reject(error);
          }

          const ignoredStatuses = [
            401,
            403,
            422,
          ];

          if (invalidRoleSchema.safeParse(error.response?.data).success) {
            await this.logout();
          } else if (!error.response?.status || !ignoredStatuses.includes(error.response?.status)) {
            console.error(`Error in axios call ${error.config?.url ?? ''}`, {
              body: JSON.stringify(error.config?.data ?? {}),
              params: JSON.stringify(error.config?.params ?? {}),
            });
          }

          return Promise.reject(error);
        } catch (_e) {
          return Promise.reject(error);
        }
      },
    );
  }

  private setAfterLoginRedirectObserver(): void {
    this.loggedOut$
      .subscribe({
        next: isAuthenticated => {
          if (this.next && isAuthenticated) {
            this.router.navigateByUrl(this.next);
            this.setNext(null);
          }
        },
      });
  }

  private csrf() {
    return axios.get(`${environment.baseUrl}/sanctum/csrf-cookie`);
  }
}
